import React, { useEffect, useState } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// JamDesktopBar — desktop JAM top header (lg only)
// Figma source: WmN2tKfRSxv1PAWTdQBkrV
//   HOST   → #586:256 "EDIT MODE_DESKTOP-TOPHEADER JAM HOST"
//   CLIENT → #588:322 "EDIT MODE_DESKTOP-TOPHEADER JAM CLIENT"
// Both frames: layoutMode NONE, fills #E4393F → absolute children on 1440 ref frame
// ─────────────────────────────────────────────────────────────────────────────

const p = (px: number) => `${(px / 1440 * 100).toFixed(3)}%`;

export const JAM_DESKTOP_BAR_H = 40; // Figma frame height (1440×40)

// Matches Tailwind `lg` — the breakpoint where the app switches to desktop chrome
export const useIsDesktopJam = (): boolean => {
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 1024px)').matches);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return isDesktop;
};

const KUMBH = 'Kumbh Sans, sans-serif';

interface JamDesktopBarProps {
  isHost: boolean;
  sessionCode?: string;
  musicianCount?: number;
  hostName?: string;
  isJamMenuOpen?: boolean;
  onToggleJamMenu?: () => void;
  jamMenuContent?: React.ReactNode;
}

const JamDesktopBar: React.FC<JamDesktopBarProps> = ({
  isHost,
  sessionCode,
  musicianCount,
  hostName,
  isJamMenuOpen = false,
  onToggleJamMenu,
  jamMenuContent,
}) => (
  <div style={{ position: 'relative', width: '100%' }}>
    <div style={{ position: 'relative', width: '100%', height: `${JAM_DESKTOP_BAR_H}px`, backgroundColor: '#E4393F', overflow: 'hidden' }}>
      {isHost ? (
        <>
          {/* GROUP #586:310 — x:60, y:8, 67.2×23.8 */}
          <div style={{ position: 'absolute', left: p(60), top: '8px', width: '67.2px', height: '23.8px' }}>
            {/* IMAGE-SVG #586:311 — x:0, y:0, 21×23.8 */}
            <img src="/figma-assets/jam-desktop/jam-icon-host.svg" width="21" height="23.8" alt="" style={{ display: 'block' }} />
            {/* TEXT #586:314 — x:35, y:2.8, 32.2×21 | Kumbh Sans 500 14px LEFT/TOP #FFFFFF */}
            <span style={{
              position: 'absolute', left: '35px', top: '2.8px', width: '32.2px', height: '21px',
              fontFamily: KUMBH, fontWeight: 500, fontSize: '14px', color: '#FFFFFF',
              textAlign: 'left', whiteSpace: 'nowrap',
            }}>
              JAM
            </span>
          </div>

          {/* GROUP #586:317 — x:555.2, y:10.8, 81.2×21 */}
          <div style={{ position: 'absolute', left: p(555.2), top: '10.8px', width: '81.2px', height: '21px' }}>
            {/* TEXT #586:318 — x:0, y:0, 70×21 */}
            <span style={{
              position: 'absolute', left: 0, top: 0, width: '70px', height: '21px',
              fontFamily: KUMBH, fontWeight: 500, fontSize: '14px', color: '#FFFFFF',
              textAlign: 'left', whiteSpace: 'nowrap',
            }}>
              HOSTING
            </span>
            {/* ELLIPSE #586:319 — x:70, y:2.8, 11.2×11.2, fill #34C759 */}
            <span style={{ position: 'absolute', left: '70px', top: '2.8px', width: '11.2px', height: '11.2px', borderRadius: '50%', backgroundColor: '#34C759' }} />
          </div>

          {/* TEXT #586:315 — x:663.4, y:10.8, 123.2×21 | Kumbh Sans 400 14px, bold segments 500 */}
          <div style={{
            position: 'absolute', left: p(663.4), top: '10.8px', height: '21px',
            fontFamily: KUMBH, fontWeight: 400, fontSize: '14px', color: '#FFFFFF', whiteSpace: 'nowrap',
          }}>
            <span style={{ fontWeight: 500 }}>CODE : </span>
            <span style={{ fontWeight: 500, color: '#FFC2C4' }}>{sessionCode || '------'}</span>
          </div>

          {/* TEXT #586:316 — x:807.6, y:10.8, 112×21 */}
          <div style={{
            position: 'absolute', left: p(807.6), top: '10.8px', height: '21px',
            fontFamily: KUMBH, fontWeight: 400, fontSize: '14px', color: '#FFFFFF', whiteSpace: 'nowrap',
          }}>
            <span style={{ fontWeight: 500 }}>MUSICIANS : </span>
            <span style={{ fontWeight: 500, color: '#FFE5E6' }}>{musicianCount ?? '—'}</span>
          </div>

          {/* IMAGE-SVG #586:309 — x:1349, y:-1, 36.4×36.4 */}
          <button
            onClick={onToggleJamMenu}
            aria-label={isJamMenuOpen ? 'Close JAM menu' : 'Open JAM menu'}
            style={{
              position: 'absolute', left: p(1349), top: '-1px', width: '36.4px', height: '36.4px',
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              transform: isJamMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s',
            }}
          >
            <img src="/figma-assets/jam-desktop/keyboard-arrow-up.svg" width="36.4" height="36.4" alt="" style={{ display: 'block' }} />
          </button>
        </>
      ) : (
        <>
          {/* IMAGE-SVG #588:386 "Union" — x:64, y:7, 24.64×24.64 */}
          <button
            onClick={onToggleJamMenu}
            aria-label="Leave JAM session"
            style={{
              position: 'absolute', left: p(64), top: '7px', width: '24.64px', height: '24.64px',
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            }}
          >
            <img src="/figma-assets/jam-desktop/client-union.svg" width="24.64" height="24.64" alt="" style={{ display: 'block' }} />
          </button>

          {/* GROUP #588:375 — x:112, y:8, 67.2×23.8 */}
          <div style={{ position: 'absolute', left: p(112), top: '8px', width: '67.2px', height: '23.8px' }}>
            {/* IMAGE-SVG #588:376 — x:0, y:0, 21×23.8 */}
            <img src="/figma-assets/jam-desktop/jam-icon-client.svg" width="21" height="23.8" alt="" style={{ display: 'block' }} />
            {/* TEXT #588:379 — x:35, y:2.8, 32.2×21 */}
            <span style={{
              position: 'absolute', left: '35px', top: '2.8px', width: '32.2px', height: '21px',
              fontFamily: KUMBH, fontWeight: 500, fontSize: '14px', color: '#FFFFFF',
              textAlign: 'left', whiteSpace: 'nowrap',
            }}>
              JAM
            </span>
          </div>

          {/* TEXT #588:392 — x:618, y:9.8, 204.36×23.4 | Kumbh Sans 500 15.6px LEFT/TOP #FFFFFF */}
          <span style={{
            position: 'absolute', left: p(618), top: '9.8px', height: '23.4px',
            fontFamily: KUMBH, fontWeight: 500, fontSize: '15.6px', color: '#FFFFFF',
            textAlign: 'left', whiteSpace: 'nowrap',
          }}>
            YOU JAM WITH {hostName || '—'}
          </span>
        </>
      )}
    </div>

    {isJamMenuOpen && jamMenuContent && (
      <div style={{ position: 'absolute', top: `${JAM_DESKTOP_BAR_H}px`, left: 0, right: 0, zIndex: 100, maxHeight: `calc(100vh - ${JAM_DESKTOP_BAR_H}px)`, overflowY: 'auto', animation: 'jam-menu-open 0.2s ease' }}>
        {jamMenuContent}
      </div>
    )}
  </div>
);

export default JamDesktopBar;
